<template>
  <a
    :id="`product-card-${product.id}`"
    class="product-card"
    :class="[
      $blockClass(blockClass, 'product-card'),
      { 'product-card--hoverable': product.variants && handleShowVariants(product) },
      className && className,
      layout && `product-card--${layout}`
    ]"
    :style="[
      productOrder && `--order: ${productOrder};`,
    ]"
    :aria-label="product.title"
    :aria-hidden="hidden ? true : undefined"
    :href="product.url"
    :data-product-listing-result-id="product.productListingResultId"
    @click="(event) => handleProductCardClick(event)"
  >
    <div
      ref="galleryContainer"
      class="product-card__content"
      :class="[
        {
          'product-card__content--video': productVideo && !filtersSelected,
          'product-card__content--gallery': showMobileGallery,
        }
      ]"
    >
      <div
        v-if="showCustomTag && blockClass !== 'complete-the-look'"
        class="product-card__tags"
      >
        <span
          class="product-card__tag text-desktop-small"
        >{{ customTag }} </span>
      </div>

      <div
        v-if="productVideo && !filtersSelected"
        class="product-card__video-container"
      >
        <video
          class="product-card__video-player"
          :autoplay="true"
          :muted="true"
          :loop="true"
          :poster="productImage.src"
          playsinline
        >
          <source :src="productVideo.src">
        </video>
      </div>

      <responsive-image
        v-else
        :class="$blockClass(blockClass, 'product-card-image')"
        block-class="product-card"
        :image="productImage"
        min-max="500-2000"
        sizes="(max-width: 767px) 100vw, (min-width: 768px) calc(100vw / 4)"
        ratio="5:7"
        @responsive-image-loaded="$emit('product-card-image-loaded', true)"
      />

      <template v-if="showMobileGallery">
        <template
          v-for="(image, index) of productImages"
        >
          <responsive-image
            v-if="image?.src && (image?.src !== productImage?.src)"
            :key="`${image.src-index}`"
            :class="$blockClass(blockClass, 'product-card-image')"
            block-class="product-card"
            :image="image"
            :lazyload="index > 1"
            min-max="500-2000"
            sizes="(max-width: 767px) 100vw, (min-width: 768px) calc(100vw / 4)"
            ratio="5:7"
            @responsive-image-loaded="$emit('product-card-image-loaded', true)"
          />
        </template>
      </template>

      <responsive-image
        v-if="hoverImage && !isMobile"
        class="product-card__hover-image-container"
        block-class="product-card"
        :image="hoverImage"
        min-max="780-2000"
        sizes="(max-width: 767px) 100vw, (min-width: 768px) calc(100vw / 4)"
        ratio="5:7"
        @responsive-image-loaded="$emit('product-card-image-loaded', true)"
      />

      <product-badge
        v-if="showBadge"
        :product="product"
      />
    </div>

    <div
      v-if="showMobileGallery"
      class="product-card__gallery-pagination"
    >
      <template v-for="(image, index) of productImages">
        <span
          v-if="image?.src"
          :key="`product-card__gallery-pagination-${index}`"
          class="product-card__gallery-pagination-item"
          :class="[
            activeGalleryImage == index &&
              'product-card__gallery-pagination-item--active'
          ]"
        />
      </template>
    </div>

    <div
      v-if="showProductTitles"
      class="product-card__footer"
      :class="[
        $blockClass(blockClass, 'product-card-footer')
      ]"
      @click="(event) => handleFooterClick(event)"
    >
      <div
        class="product-card__footer-content"
      >
        <div class="product-card__description">
          <a
            class="
              product-card__title
              product-card__link
              text-desktop-small-desktop
              text-mobile-small-mobile
              text-regular
            "
            :href="product.url"
            :class="$blockClass(blockClass, 'product-card-title')"
            v-text="product.title"
          />
          <div
            v-if="handleShowVariants(product)"
            class="product-card__variants"
          >
            <ul
              v-if="!allVariantsUnavailable && !isComingSoon"
              class="product-card__variant-list text-desktop-small"
            >
              <li
                v-for="(variant, index) of productVariantSorted"
                :key="`${variant.id}-${index}`"
                class="product-card__variant"
                data-gtm-event="quick-add"
              >
                <button
                  v-if="product.variants.length"
                  :data-title="variant.title"
                  type="button"
                  class="
                  product-card__variant-size
                  depict-add-to-cart
                  "
                  :class="[
                    {
                      [$class('disabled')]: !variant.available
                    },
                  ]"
                  :data-id="variant.variantId"
                  @click="(event) => enableQuickAdd && handleAddToCart(event, variant)"
                  v-text="variant.title"
                />
              </li>
            </ul>

            <span
              v-if="isComingSoon"
              class="
            product-card__coning-soon
            text-desktop-small-desktop
            text-mobile-small-mobile
          "
              v-text="$string('product.prices.coming_soon')"
            />
            <span
              v-else-if="allVariantsUnavailable"
              class="
            product-card__oos
            text-desktop-small-desktop
            text-mobile-small-mobile
          "
              v-text="$string('product.prices.sold_out')"
            />
          </div>

          <product-prices
            :class="$blockClass(blockClass, 'product-card-prices')"
            block-class="product-card"
            :compare-at-price="product?.compareAtPrice?.amount"
            :price="product?.price?.amount"
          />
        </div>
      </div>

      <!--
        <div class="product-card__wishlist">
        <wishlist-button :product="product" />
        </div>
      -->
    </div>

    <product-swatches
      v-if="!hideColorSwatches"
      :product="product"
      :load-swatches="productCardShown"
      :show-only-text="isMobile"
      :show-active-swatch="false"
      show-hover-image
      :swatch-limit="3"
      block-class="product-card"
    />
  </a>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} blockClass - Block level class to apply to all elements.
 * @param {String} className - Additional class to apply to product card.
 * @param {String} collection - Handle of collection for product card grid.
 * @param {Boolean} filtersSelected - Checks if filters are selected in PLP
 * @param {String} layout - Layout of container grid.
 * @param {Boolean|Object} product - Product object.
 * @param {Boolean} enableQuickAdd - Enable add to cart functionality
 * @param {Boolean} showBadge - Show product badge
 * @param {Boolean} showFlatImage - Show flat image by default
 * @param {Boolean} showProductTitles - Show product titles
 * @param {Boolean} showVariants - Show product variants
 * @param {Boolean} productOrder - Order of product in grid
 * @param {Boolean} hideColorSwatches - Hide color swatches
 * @param {Boolean} hidden - Hide product card or not
 * @param {Boolean} mobileGallery - Show mobile gallery
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'

import ProductSwatches from '~async/product-swatches/product-swatches'
import ProductBadge from '~async/product-badge/product-badge'

export default {
  name: 'ProductCard',

  components: {
    ProductPrices,
    ResponsiveImage,
    ProductBadge,
    ProductSwatches,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    className: {
      type: String,
      default: '',
    },
    collection: {
      type: String,
      default: null,
    },
    disableCustomTag: {
      type: String,
      default: '',
    },
    filtersSelected: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: String,
      default: null,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    enableQuickAdd: {
      type: Boolean,
      default: true,
    },
    showBadge: {
      type: Boolean,
      default: true,
    },
    showFlatImage: {
      type: Boolean,
      default: false,
    },
    disableImageHover: {
      type: Boolean,
      default: false,
    },
    showProductTitles: {
      type: Boolean,
      default: true,
    },
    showVariants: {
      type: Boolean,
      default: true,
    },
    productOrder: {
      type: [Boolean, Number],
      default: false,
    },
    hideColorSwatches: {
      type: Boolean,
      default: false,
    },
    hidden: {
      type: Boolean,
      default: false,
    },
    mobileGallery: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'product-card-image-loaded': (payload) => payload,
  },

  data() {
    return {
      lineItemProperties: {},
      quantity: 1,
      state: 'ready',
      timeout: {},
      productCardShown: false,
      activeGalleryImage: 0,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * returns custom tag value from product tags
     * if a product tag starts with 'custom:'
     * it will return the value after the colon
     * @returns {String}
     */
    customTag() {
      const customTag = this.product?.tags?.find(tag => tag.startsWith('custom:'))
      const customTagValue = customTag ? customTag.split('custom:')[1] : ''
      return customTagValue ? customTagValue : ''
    },

    /**
     * Show custom tag if product has custom tag
     * and custom tag is not disabled
     * @returns {String}
     */
    showCustomTag() {
      return this.customTag && this.disableCustomTag !== this.customTag
    },

    /**
     * Compute layout for flat images.
     * @returns {Boolean}
     */
    flatLayout() {
      return Boolean(this.layout === 'overview' ||
        this.showFlatImage ||
        this.product.flatImageCollections?.includes(this.collection))
    },

    /**
     * Check if all variants are available.
     */
    allVariantsUnavailable() {
      return this.product?.variants?.every((variant) => variant.available === false)
    },

    /**
     * Compute if product has coming-soon tag
     * @returns {Boolean}
     */
    isComingSoon() {
      return this.product?.tags?.includes('coming-soon')
    },

    /**
     * Compute product color from product tags
     * @returns {String}
     */
    productColor() {
      const colorTag = this.product?.tags?.find((tag) => {
        return tag.includes('color:')
      })

      return colorTag?.replace('color:', '')
    },

    /**
     * Compute product image based on alt text
     * - Uses the image alt text to set the product image according to
     * layout and collection.
     * - Sample format for alt text values:
     * `### main_image:new-in,denim || flat_image`
     * @returns {Object}
     */
    productImage() {
      const product = this.product
      const productImages = product.images

      let defaultImage = product.featuredImage || productImages[0]

      productImages?.forEach(image => {
        if (!image.alt) {
          return
        }

        const imageTags = image.alt.split('||')

        imageTags.forEach(tag => {
          if (this.flatLayout) {

            // Returns flat image if product card is set to show flat image or
            // if current layout is set as overview
            if (tag.includes('flat_image:')) {

              // Returns image if alt text contains '### flat_image:' on small
              // grid and matches the current collection
              if (this.collection) {
                const tagArray = tag.split('flat_image:')
                const collections = tagArray[tagArray.length - 1].split(',')

                if (collections.some(col => col.includes(this.collection))) {
                  defaultImage = image
                }
              }
            } else if (tag.includes('flat_image')) {
              defaultImage = image
            }
          } else if (tag.includes('main_image:')) {

            // Returns image if alt text contains '### main_image:' on large and
            // medium grids and matches the current collection
            if (this.collection) {
              const tagArray = tag.split('main_image:')
              const collections = tagArray[tagArray.length - 1].split(',')

              if (collections.some(col => col.includes(this.collection))) {
                defaultImage = image
              }
            }
          }
        })
      })

      if (this.flatLayout && !this.product?.featuredImageIsFlat) {
        // The images come from product.media or from product.images
        // So we need to account for both cases
        if (product.media) {
          defaultImage = product.media[product.media.length - 1]
        } else {
          defaultImage = productImages[productImages.length - 1]
        }

      }

      return {
        ...defaultImage,
        alt: this.getImageAltText(this.product, defaultImage),
      }
    },

    productImages() {
      let images = []

      if (this.product.media) {
        images = this.product.media
      } else {
        images = this.product.images
      }

      return images.filter(image => image)
    },

    /**
     * Checks if product has hover image
    * @returns {Object|Boolean}
     */
    hoverImage() {
      let images = []
      let hoverImage = false

      if (this.product.media) {
        images = this.product.media
      } else {
        images = this.product.images
      }

      if (this.disableImageHover || !images[1]) {
        return false
      }

      /**
       * If featured image is flat, show the second image in images array.
       * If flat layout is enabled, show the first image in images array.
       * Otherwise show the last image in the array.
       */
      if (this.product?.featuredImageIsFlat) {
        hoverImage = images[1]
      } else if (this.flatLayout) {
        hoverImage = images[0]
      } else {
        hoverImage = images[images.length - 1]
      }

      return hoverImage
    },

    /**
     * Compute product video based on alt text
     * - Sets product video as main background if the alt text contains
     *  the tag `### main_video:[collection_handle]
     * @returns {Boolean|String}
     */
    productVideo() {
      if (this.flatLayout || !this.collection) {
        return false
      }

      const product = this.product
      const productMedia = product.media

      const video = productMedia?.find((media) => media?.media_type === 'video')
      const videoAlt = video?.alt

      // Product video is only returned if the alt text contains main_video,
      // the specified collection is equal to current collection and
      // the layout is not overview
      if (!video || !videoAlt?.includes('main_video')) {
        return false
      }

      const tagArray = videoAlt.split('main_video:')
      const collections = tagArray[tagArray.length - 1].split(',')

      if (!collections.some(col => col.includes(this.collection))) {
        return false
      }

      return video
    },

    productVariantSorted() {
      const textSizes = ['XXXS', 'XXS', 'XS', 'XS/S', 'S', 'S/M', 'M', 'M/L', 'L', 'L/XL', 'XL', 'XXL', 'XXXL', 'T1', 'T2', 'T3', 'T4', 'T5']
      const variants = this.product?.variants

      const sortedVariants = variants.sort((a, b) => {
        const aSize = a.title
        const bSize = b.title

        // Check for S-suffix sizes like "28S", "30S", etc.
        const aSMatch = aSize.match(/^(?<number>\d+)S$/)
        const bSMatch = bSize.match(/^(?<number>\d+)S$/)

        if (aSMatch && bSMatch) {
          // Compare numerically if both are in the format "28S", "30S", etc.
          return parseInt(aSMatch.groups.number) - parseInt(bSMatch.groups.number)
        }
        if (aSMatch) {
          // Place S-suffix sizes after numeric sizes but before text sizes
          return isNaN(bSize) ? -1 : 1
        }
        if (bSMatch) {
          // Place S-suffix sizes after numeric sizes but before text sizes
          return isNaN(aSize) ? 1 : -1
        }

        // Check if both sizes are jean sizes in the format "waist/inseam"
        const aIsJeanSize = aSize.includes('/') && aSize.split('/').length === 2
        const bIsJeanSize = bSize.includes('/') && bSize.split('/').length === 2

        if (aIsJeanSize && bIsJeanSize) {
          const [aWaist, aInseam] = aSize.split('/').map(Number)
          const [bWaist, bInseam] = bSize.split('/').map(Number)

          // Compare by waist size first, then by inseam size
          if (aWaist !== bWaist) {
            return aWaist - bWaist
          }
          return aInseam - bInseam
        }

        // If both sizes are numbers (but not jean sizes), compare numerically
        if (!isNaN(aSize) && !isNaN(bSize)) {
          return parseFloat(aSize) - parseFloat(bSize)
        }

        if (typeof aSize === 'string' && typeof bSize === 'string') {
          const aIndex = textSizes.indexOf(aSize)
          const bIndex = textSizes.indexOf(bSize)

          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex
          }
        }

        return 0
      })
      return sortedVariants
    },

    showMobileGallery() {
      return this.isMobile && this.mobileGallery && this.layout !== 'overview'
    },
  },

  watch: {
    productImage: {
      handler() {
        if (!this.showMobileGallery || !this.$refs.galleryContainer) {
          return
        }

        /*
        * @TODO: Investigate a better way to handle when the images within
        * $refs.galleryContainer has been completely rendered
        */
        setTimeout(() => {
          // When layout changes from overview to grid, re-init the slider
          this.handleActiveSlide()
        }, 100)
      },
    },
    productImages: {
      handler() {
        if (!this.showMobileGallery || !this.$refs.galleryContainer) {
          return
        }

        /*
         * Reset scroll position and re-init the slider when product images
         * changes when using the filter
        */
        this.$refs.galleryContainer.scrollLeft = 0

        /*
        * @TODO: Investigate a better way to handle when the images within
        * $refs.galleryContainer has been completely rendered after filtering
        */
        setTimeout(() => {
          this.handleActiveSlide()
        }, 100)
      },
    },
  },

  mounted() {

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.productCardShown = true
          observer.unobserve(entry.target)
        }
      })
    })

    observer.observe(this.$el)

    this.handleActiveSlide()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Sets the product ID to session storage, for use when returning to PLP.
     */
    handleProductCardClick(event, product) {
      this.handleGtm(event, product)
      this.selectProduct()
    },

    /**
     * Set active slide in mobile pagination
     */
    handleActiveSlide() {
      if (!this.showMobileGallery) {
        return
      }

      const images = this.$refs.galleryContainer.querySelectorAll('.product-card__image-container')

      images.forEach((image, index) => {
        const observer = new IntersectionObserver((entries) => {
          const entry = entries[0]

          if (entry.isIntersecting) {
            this.activeGalleryImage = index
          }
        }, { threshold: 0.5 })

        observer.observe(image)
      })
    },

    handleGtm(event) {
      const target = event.target
      const actionIsQuickAdd = target.closest('[data-gtm-event="quick-add"]')
      const placementIsCompleteTheLook = target.closest('[data-gtm-event="complete-the-look"]')
      const placementIsProductCarousel = target.closest('[data-gtm-event="product-carousel-slide"]')
      const placementIsRecentlyViewedProducts = target.closest('[data-gtm-event="recently-viewed-products"]')
      const placementIsRecommendedProducts = target.closest('[data-gtm-event="recommended-products"]')

      if (actionIsQuickAdd) {
        this.trackQuickAdd(target)
      } else if (placementIsCompleteTheLook) {
        this.trackCompleteTheLookClick()
      } else if (placementIsProductCarousel) {
        this.trackProductCarouselClick(target)
      } else if (placementIsRecentlyViewedProducts) {
        this.trackRecentlyViewedProductsClick()
      } else if (placementIsRecommendedProducts) {
        this.trackRecommendedProductsClick()
      } else {
        this.trackProductCardClick()
      }
    },

    trackQuickAdd(target) {
      const dataLayer = window.dataLayer || []
      const { handle, id, price, title } = this.product
      const url = document.location.pathname
      const template = url.split('/')[1]

      dataLayer.push({
        event: 'quick_add',
        quickAdd: {
          template,
          url,
          item: {
            handle,
            id,
            title,
            price: price.amount,
            currency: price.currencyCode,
            variant: target.innerText,
          },
        },
      })
    },

    trackProductCardClick() {
      const dataLayer = window.dataLayer || []
      const { handle, id, price, title } = this.product
      const url = document.location.pathname
      const template = url.split('/')[1]

      dataLayer.push({
        event: 'product_card_click',
        productCardClick: {
          template,
          url,
          item: {
            handle,
            id,
            title,
            price: price.amount,
            currency: price.currencyCode,
          },
        },
      })
    },

    trackCompleteTheLookClick() {
      const dataLayer = window.dataLayer || []
      const { handle, id, price, title } = this.product
      const url = document.location.pathname

      dataLayer.push({
        event: 'complete_the_look_click',
        completeTheLookClick: {
          url,
          item: {
            handle,
            id,
            title,
            price: price.amount,
            currency: price.currencyCode,
          },
        },
      })
    },

    trackProductCarouselClick(target) {
      const dataLayer = window.dataLayer || []
      const { handle, id, price, title } = this.product
      const url = document.location.pathname
      const slide = target.closest('[data-gtm-event="product-carousel-slide"]')

      dataLayer.push({
        event: 'product_carousel_click',
        productCarouselClick: {
          url,
          click_index: slide.ariaLabel,
          item: {
            handle,
            id,
            title,
            price: price.amount,
            currency: price.currencyCode,
          },
        },
      })
    },

    trackRecentlyViewedProductsClick() {
      const dataLayer = window.dataLayer || []
      const { handle, id, price, title } = this.product
      const url = document.location.pathname

      dataLayer.push({
        event: 'recently_viewed_products_click',
        recentlyViewedProductsClick: {
          url,
          item: {
            handle,
            id,
            title,
            price: price.amount,
            currency: price.currencyCode,
          },
        },
      })
    },

    trackRecommendedProductsClick() {
      const dataLayer = window.dataLayer || []
      const product = this.product
      const url = document.location.pathname

      dataLayer.push({
        event: 'recommended_products_click',
        recommendedProductsClick: {
          url,
          item: {
            handle: product.handle,
            id: product.id,
            price: product.price.amount,
            currency: product.price.currencyCode,
          },
        },
      })
    },

    selectProduct() {
      sessionStorage.setItem('selected_product_id', this.product.id)
    },

    /**
     * Check if variant option is size
     * @param {Object} variant - Product variant object.
     * @returns {Boolean}
     */
    sizeVariant(variant) {
      return variant?.selectedOptions.some(option => option.name === 'Size')
    },

    /**
     * Prevents opening product page if footer is clicked
     * @param {Event} event - Click event.
     */
    handleFooterClick(event) {
      if (!this.isComingSoon || !this.handleShowVariant(this.product)) {
        return
      }
      event.preventDefault()
    },

    /**
     * Gets image alt text from product title or alt tag
     * @param {Object} product - Product object.
     * @param {Object} image - Product image object.
     * @returns {String}
     */
    getImageAltText(product, image) {
      let altText = product.title

      const imageTags = image?.alt?.split('||')
      const altTag = imageTags?.find(tag => tag.includes('alt:'))

      if (altTag) {
        const tagArray = altTag.split('alt:')
        altText = tagArray[tagArray.length - 1]
      }

      return altText
    },

    /**
     * Checks if product variants should be productCardShown on hover
     */
    handleShowVariants(product) {
      // @TODO: Use setting instead of hard coded values
      const productTypes = ['denim', 'jeans']

      if (this.showVariants && !productTypes?.includes(product?.type?.toLowerCase())) {
        return true
      }

      return false
    },

    /**
     * Handle add to cart submission.
     * @param {Event} event - Click event.
     */
    async handleAddToCart(event, variant) {
      event.preventDefault()

      window.dpc.sendAddToCartEvent({ product_id: `[${variant.variantId}]` })

      if (!variant || this.state !== 'ready') {
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      const items = [
        {
          id: Number(variant.variantId),
          properties: this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        if (!this.isMobile && this.getComponentExists('cart-drawer')) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.error = error.description
          return
        }

        reportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'))
      })

      this.state = 'ready'
    },
  },

}
</script>

<style lang="scss">
@import './product-card';
</style>
